import React, { useEffect, useState, useMemo } from "react";
import { db } from "@/lib/db";
import { useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TrendingUp, TrendingDown, Wallet, FileText, ArrowUp, ArrowDown } from "lucide-react";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend, LineChart, Line } from "recharts";
import { fmtBRL } from "@/lib/supabase";
import { Button } from "@/components/ui/button";
import { jsPDF } from "jspdf";
import "jspdf-autotable";
import * as XLSX from "xlsx";

const MetricCard = ({ label, valA, valB, isCurrency = true, inverseDelta = false }) => {
  const diff = valA - valB;
  const pct = valB === 0 ? (valA > 0 ? 100 : 0) : (diff / valB) * 100;
  
  let isPositive = diff > 0;
  let isNegative = diff < 0;
  
  if (inverseDelta) {
    isPositive = diff < 0;
    isNegative = diff > 0;
  }

  const color = isPositive ? 'text-green-600' : isNegative ? 'text-red-600' : 'text-[#8A8A8A]';
  const Icon = isPositive ? ArrowUp : isNegative ? ArrowDown : TrendingUp;

  return (
    <Card className="p-6 bg-white border border-black/10 rounded-none flex flex-col justify-between">
      <div className="font-mono text-xs uppercase tracking-widest text-[#8A8A8A] mb-4">{label}</div>
      <div className="flex gap-8 mb-4">
        <div>
          <div className="text-[10px] uppercase text-[#8A8A8A] font-mono">Período A</div>
          <div className="font-serif text-2xl">{isCurrency ? fmtBRL(valA) : valA}</div>
        </div>
        <div>
          <div className="text-[10px] uppercase text-[#8A8A8A] font-mono">Período B</div>
          <div className="font-serif text-2xl text-[#8A8A8A]">{isCurrency ? fmtBRL(valB) : valB}</div>
        </div>
      </div>
      <div className={`text-xs flex items-center gap-1 font-mono ${color}`}>
        <Icon className="w-3 h-3" />
        {isCurrency ? fmtBRL(Math.abs(diff)) : Math.abs(diff)} ({Math.abs(pct).toFixed(1)}%)
      </div>
    </Card>
  );
};

export default function AnaliseComparativa() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  
  const d = new Date();
  const currentMonthStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  
  d.setMonth(d.getMonth() - 1);
  const prevMonthStr = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;

  const [periodA, setPeriodA] = useState(currentMonthStr);
  const [periodB, setPeriodB] = useState(prevMonthStr);

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const [financeiroData, propostasData] = await Promise.all([
          db.select("financeiro").catch(() => []),
          db.select("propostas").catch(() => []),
        ]);
        setData({
          financeiro: Array.isArray(financeiroData) ? financeiroData : [],
          propostas: Array.isArray(propostasData) ? propostasData : [],
        });
      } catch (err) {
        console.error("Erro dashboard:", err);
      }
    })();
  }, [user]);

  const m = useMemo(() => {
    if (!data) return null;
    const { financeiro } = data;

    const filterByMonth = (items, monthStr) => {
      return items.filter(f => {
        const dateStr = f.data || f.created_at || "";
        return dateStr.startsWith(monthStr);
      });
    };

    const entradas = financeiro.filter(f => f && f.tipo === "entrada" && f.status === "pago");
    const saidas = financeiro.filter(f => f && f.tipo === "saida" && f.status === "pago");

    const entradasA = filterByMonth(entradas, periodA);
    const entradasB = filterByMonth(entradas, periodB);
    const saidasA = filterByMonth(saidas, periodA);
    const saidasB = filterByMonth(saidas, periodB);

    const recA = entradasA.reduce((s, f) => s + Number(f.valor || 0), 0);
    const recB = entradasB.reduce((s, f) => s + Number(f.valor || 0), 0);
    
    const despA = saidasA.reduce((s, f) => s + Number(f.valor || 0), 0);
    const despB = saidasB.reduce((s, f) => s + Number(f.valor || 0), 0);

    const lucroA = recA - despA;
    const lucroB = recB - despB;

    // Timeline 24 meses
    const chart = [];
    const baseDate = new Date();
    for (let i = 23; i >= 0; i--) {
      const d = new Date(baseDate.getFullYear(), baseDate.getMonth() - i, 1);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
      const label = d.toLocaleDateString("pt-BR", { month: "short", year: "2-digit" });
      
      const r = filterByMonth(entradas, key).reduce((s, f) => s + Number(f.valor || 0), 0);
      const s = filterByMonth(saidas, key).reduce((sum, f) => sum + Number(f.valor || 0), 0);
      
      chart.push({
        month: label,
        revenue: Math.round(r * 100) / 100,
        expense: Math.round(s * 100) / 100,
        profit: Math.round((r - s) * 100) / 100
      });
    }

    // Top Produtos/Categorias
    const agrupar = (lista) => {
      const map = {};
      lista.forEach(i => {
        const cat = i.categoria || i.servico || "Outros";
        map[cat] = (map[cat] || 0) + Number(i.valor || 0);
      });
      return Object.entries(map).map(([name, valor]) => ({ name, valor })).sort((a,b) => b.valor - a.valor);
    };

    const catEntradasA = agrupar(entradasA);
    const catEntradasB = agrupar(entradasB);
    const catSaidasA = agrupar(saidasA);

    // Insights AI (Deterministic)
    const insights = [];
    if (recA > recB) {
      insights.push(`Seu faturamento aumentou ${(((recA - recB)/recB)*100).toFixed(1)}% em relação ao período B.`);
    } else if (recA < recB) {
      insights.push(`Atenção: seu faturamento caiu ${(((recB - recA)/recB)*100).toFixed(1)}% comparado ao período B.`);
    }

    if (despA > despB) {
      insights.push(`Os gastos cresceram ${(((despA - despB)/despB)*100).toFixed(1)}%, impactando a margem.`);
    }

    if (catEntradasA.length > 0) {
      insights.push(`${catEntradasA[0].name} foi o serviço que mais gerou receita no Período A.`);
    }

    return {
      recA, recB, despA, despB, lucroA, lucroB, chart,
      catEntradasA, catEntradasB, catSaidasA, insights
    };
  }, [data, periodA, periodB]);

  const exportPDF = () => {
    if (!m) return;
    const doc = new jsPDF();
    doc.text("Relatorio de Analise Comparativa", 14, 15);
    
    doc.autoTable({
      startY: 25,
      head: [['Métrica', 'Período A', 'Período B', 'Diferença']],
      body: [
        ['Receita', fmtBRL(m.recA), fmtBRL(m.recB), fmtBRL(m.recA - m.recB)],
        ['Despesas', fmtBRL(m.despA), fmtBRL(m.despB), fmtBRL(m.despA - m.despB)],
        ['Lucro', fmtBRL(m.lucroA), fmtBRL(m.lucroB), fmtBRL(m.lucroA - m.lucroB)],
      ],
    });
    doc.save("analise_comparativa.pdf");
  };

  const exportExcel = () => {
    if (!m) return;
    const ws = XLSX.utils.json_to_sheet([
      { Metrica: "Receita", PeriodoA: m.recA, PeriodoB: m.recB, Diferenca: m.recA - m.recB },
      { Metrica: "Despesas", PeriodoA: m.despA, PeriodoB: m.despB, Diferenca: m.despA - m.despB },
      { Metrica: "Lucro", PeriodoA: m.lucroA, PeriodoB: m.lucroB, Diferenca: m.lucroA - m.lucroB },
    ]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Analise");
    XLSX.writeFile(wb, "analise_comparativa.xlsx");
  };

  const exportCSV = () => {
    if (!m) return;
    const csvContent = "data:text/csv;charset=utf-8," 
      + "Metrica,PeriodoA,PeriodoB,Diferenca\n"
      + `Receita,${m.recA},${m.recB},${m.recA - m.recB}\n`
      + `Despesas,${m.despA},${m.despB},${m.despA - m.despB}\n`
      + `Lucro,${m.lucroA},${m.lucroB},${m.lucroA - m.lucroB}`;
    
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", "analise.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (!data || !m) return <div className="text-[#8A8A8A] font-mono text-sm p-6">Carregando módulo...</div>;

  const doubleChartData = [
    { name: "Receita", valA: m.recA, valB: m.recB },
    { name: "Despesas", valA: m.despA, valB: m.despB },
    { name: "Lucro", valA: m.lucroA, valB: m.lucroB },
  ];

  const monthOptions = Array.from({ length: 60 }).map((_, i) => {
    const d = new Date();
    d.setDate(1);
    d.setMonth(d.getMonth() - i);
    const val = d.toISOString().substring(0, 7);
    const label = d.toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
    return { val, label };
  });

  return (
    <div>
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end mb-8 gap-4">
        <div>
          <div className="font-mono text-xs uppercase tracking-widest text-[#D9381E] mb-2">Painel</div>
          <h1 className="font-serif text-4xl md:text-5xl">Análise Comparativa</h1>
          <p className="text-[#4A4A4A] mt-2">Compare períodos inteligentemente e entenda a evolução do seu negócio.</p>
        </div>
        
        <div className="flex gap-4">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-tighter text-[#8A8A8A] mb-1">Período A</div>
            <Select value={periodA} onValueChange={setPeriodA}>
              <SelectTrigger className="rounded-none w-40 bg-white border-black/10 h-10 capitalize">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {monthOptions.map(o => <SelectItem key={o.val} value={o.val} className="capitalize">{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <div className="text-[10px] font-mono uppercase tracking-tighter text-[#8A8A8A] mb-1">Período B</div>
            <Select value={periodB} onValueChange={setPeriodB}>
              <SelectTrigger className="rounded-none w-40 bg-white border-black/10 h-10 capitalize">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {monthOptions.map(o => <SelectItem key={o.val} value={o.val} className="capitalize">{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-4 mb-8">
        <MetricCard label="Receita" valA={m.recA} valB={m.recB} />
        <MetricCard label="Despesas" valA={m.despA} valB={m.despB} inverseDelta />
        <MetricCard label="Lucro Líquido" valA={m.lucroA} valB={m.lucroB} />
      </div>

      <div className="grid md:grid-cols-3 gap-8 mb-8">
        <Card className="md:col-span-2 p-6 bg-white border border-black/10 rounded-none h-96">
          <div className="font-mono text-xs uppercase tracking-widest text-[#8A8A8A] mb-4 flex justify-between">
            Comparativo Direto (A vs B)
            <div className="flex gap-2">
              <Button size="sm" variant="outline" className="h-6 text-[10px] rounded-none" onClick={exportPDF}>PDF</Button>
              <Button size="sm" variant="outline" className="h-6 text-[10px] rounded-none" onClick={exportExcel}>Excel</Button>
              <Button size="sm" variant="outline" className="h-6 text-[10px] rounded-none" onClick={exportCSV}>CSV</Button>
            </div>
          </div>
          <ResponsiveContainer width="100%" height="85%">
            <BarChart data={doubleChartData}>
              <CartesianGrid strokeDasharray="2 4" stroke="#E5E3DB" />
              <XAxis dataKey="name" stroke="#4A4A4A" fontSize={12} />
              <YAxis stroke="#4A4A4A" fontSize={12} />
              <Tooltip formatter={(v) => fmtBRL(v)} contentStyle={{ fontFamily: "Manrope", borderRadius: 0 }} />
              <Legend wrapperStyle={{ fontSize: "12px", color: "#4A4A4A" }} />
              <Bar dataKey="valA" name="Período A" fill="#1A1A1A" />
              <Bar dataKey="valB" name="Período B" fill="#D9381E" />
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card className="p-6 bg-white border border-black/10 rounded-none overflow-y-auto h-96">
          <div className="font-mono text-xs uppercase tracking-widest text-[#8A8A8A] mb-4">Insights de IA (Determinístico)</div>
          <div className="flex flex-col gap-4">
            {m.insights.map((insight, idx) => (
              <div key={idx} className="p-4 bg-[#F6F5F0] border-l-2 border-[#D9381E] text-sm text-[#1A1A1A]">
                {insight}
              </div>
            ))}
            {m.insights.length === 0 && (
              <div className="text-sm text-[#8A8A8A]">Não há dados suficientes para gerar insights.</div>
            )}
          </div>
        </Card>
      </div>

      <div className="grid md:grid-cols-2 gap-8 mb-8">
        <Card className="p-6 bg-white border border-black/10 rounded-none h-80">
          <div className="font-mono text-xs uppercase tracking-widest text-[#8A8A8A] mb-4">Top 5 Categorias: Entradas (Período A)</div>
          {m.catEntradasA.length > 0 ? (
            <ResponsiveContainer width="100%" height="80%">
              <BarChart data={m.catEntradasA.slice(0, 5)} layout="vertical" margin={{ top: 0, right: 0, left: 30, bottom: 0 }}>
                <XAxis type="number" hide />
                <YAxis dataKey="name" type="category" axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: '#4A4A4A' }} />
                <Tooltip cursor={{ fill: 'transparent' }} formatter={(val) => fmtBRL(val)} />
                <Bar dataKey="valor" fill="#1A1A1A" radius={[0, 4, 4, 0]} barSize={20} />
              </BarChart>
            </ResponsiveContainer>
          ) : <div className="text-sm text-[#8A8A8A] mt-10 text-center">Sem dados</div>}
        </Card>
        <Card className="p-6 bg-white border border-black/10 rounded-none h-80">
          <div className="font-mono text-xs uppercase tracking-widest text-[#8A8A8A] mb-4">Top 5 Categorias: Saídas (Período A)</div>
          {m.catSaidasA.length > 0 ? (
            <ResponsiveContainer width="100%" height="80%">
              <BarChart data={m.catSaidasA.slice(0, 5)} layout="vertical" margin={{ top: 0, right: 0, left: 30, bottom: 0 }}>
                <XAxis type="number" hide />
                <YAxis dataKey="name" type="category" axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: '#4A4A4A' }} />
                <Tooltip cursor={{ fill: 'transparent' }} formatter={(val) => fmtBRL(val)} />
                <Bar dataKey="valor" fill="#D9381E" radius={[0, 4, 4, 0]} barSize={20} />
              </BarChart>
            </ResponsiveContainer>
          ) : <div className="text-sm text-[#8A8A8A] mt-10 text-center">Sem dados</div>}
        </Card>
      </div>

      <Card className="p-6 bg-white border border-black/10 rounded-none mb-8">
        <div className="font-mono text-xs uppercase tracking-widest text-[#8A8A8A] mb-4">Evolução Histórica (Últimos 24 meses)</div>
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={m.chart}>
              <CartesianGrid strokeDasharray="2 4" stroke="#E5E3DB" />
              <XAxis dataKey="month" stroke="#4A4A4A" fontSize={12} />
              <YAxis stroke="#4A4A4A" fontSize={12} />
              <Tooltip formatter={(v) => fmtBRL(v)} contentStyle={{ fontFamily: "Manrope", borderRadius: 0 }} />
              <Legend wrapperStyle={{ fontSize: "12px", color: "#4A4A4A" }} />
              <Line type="monotone" dataKey="revenue" name="Receita" stroke="#1A1A1A" strokeWidth={2} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="expense" name="Saídas" stroke="#D9381E" strokeWidth={2} dot={{ r: 3 }} />
              <Line type="monotone" dataKey="profit" name="Lucro" stroke="#16A34A" strokeWidth={2} dot={{ r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Card>
    </div>
  );
}

